// @flow
import React, { type Node } from 'react';
import I18nHelper from 'common/helpers/I18nHelper';
import { Flex, Button } from '@locus-taxy/neo-ui';
import entityIds from 'common/tests/entity/entityIds.json';
import CloneableFieldList from 'common/components/core/fieldList/CloneableFieldList';
import { ReactTelInput } from 'common/components/core/react_tel_input';
import type { PhoneNumber } from 'common/models/internal/PhoneNumber';
import type { List } from 'immutable';
const nsTranslate = I18nHelper.getTranslate(['entityManagement']);

type ButtonProps = {
    isDisabled: boolean,
    onClick: Event => void,
    buttonText?: string
};

const renderButton = ({
    isDisabled,
    buttonText,
    ...rest
}: ButtonProps): Node => (
    <Button
        {...rest}
        appearance="ghost"
        palette="primary"
        disabled={isDisabled}
        sx={{
            fontSize: '1.2rem',
            minHeight: 'unset'
        }}
        testId={entityIds.inputs.addAlternateMobileNumber}
    >{`+ ${
        buttonText || nsTranslate('contactForm.addAlternateMobileNumber')
    }`}</Button>
);

type PhoneNumberInputProps = {
    showDelete: boolean,
    className: string,
    index: number,
    value: string,
    field: any,
    onDeleteField: number => void,
    onChange: (string, number) => void,
    disabled?: boolean
};

const renderPhoneNumberInput = ({
    showDelete,
    className,
    index,
    onChange,
    value,
    field,
    onDeleteField,
    disabled,
    ...rest
}: PhoneNumberInputProps): Node => {
    const onInputChange = value => {
        onChange(value, index);
    };
    const onDelete = () => {
        onDeleteField(index);
    };
    return (
        <Flex alignItems="center" key={index} className={className}>
            <ReactTelInput
                {...rest}
                error={
                    field.validationMessage &&
                    value.get(index)?.validatePhoneNumber()
                }
                value={value.getIn([index, 'phoneNumber'])}
                onChange={onInputChange}
                disabled={disabled}
            />
            {showDelete && (
                <Button
                    appearance="icon"
                    icon="delete"
                    color="grey.500"
                    ml={1}
                    sx={{
                        '&:hover': {
                            background: 'transparent',
                            borderColor: 'transparent'
                        },
                        '&:focus': {
                            background: 'transparent',
                            borderColor: 'transparent'
                        }
                    }}
                    onClick={onDelete}
                    disabled={disabled}
                />
            )}
        </Flex>
    );
};

type Props = {
    onDelete: number => void,
    onChange: (val: string, index: number) => void,
    value: List<PhoneNumber>,
    field: any,
    buttonText?: string
};

const ContactNumberInputList = (props: Props): Node => {
    const { onDelete: onDeleteHandler, value } = props;
    const onDelete = ([fieldIndex]) => {
        onDeleteHandler(fieldIndex);
    };
    return (
        <CloneableFieldList
            {...props}
            initialFieldCount={value.size || 1}
            maxFieldCount={10}
            renderField={renderPhoneNumberInput}
            renderAddFieldButton={renderButton}
            onDelete={onDelete}
        />
    );
};

export default ContactNumberInputList;
